import { Download, ExternalLink, FileText, File, Link as LinkIcon, Image as ImageIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import type { Project } from '@/services/types';
import { getMediaType } from '@/lib/utils';

const MediaTab = ({ data }: { data?: Project }) => {
  const [imageError, setImageError] = useState(false);

  const handleDownload = (link: string) => {
    const a = document.createElement('a');
    a.href = link;
    a.download = data?.media?.name ?? 'File';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Check if media exists
  const hasMedia = data?.media?.url && data?.media?.url !== '';

  const mediaType = hasMedia ? getMediaType(data?.media?.url || '') : null;

  const renderMediaPreview = () => {
    if (!hasMedia || !mediaType) return null;

    const url = data?.media?.url || '';

    switch (mediaType) {
      case 'image':
        return (
          <div className='relative h-40 w-full rounded border bg-gray-50'>
            {!imageError ? (
              <img
                src={url}
                alt={data?.media?.name || 'Media preview'}
                className='h-full w-full object-cover'
                onError={() => setImageError(true)}
              />
            ) : (
              <div className='flex h-full w-full items-center justify-center'>
                <ImageIcon className='h-12 w-12 text-gray-400' />
              </div>
            )}
          </div>
        );
      
      case 'pdf':
        return (
          <div className='flex h-40 w-full flex-col items-center justify-center rounded border bg-red-50'>
            <FileText className='h-16 w-16 text-red-500' />
            <p className='mt-2 text-sm font-medium text-gray-700'>PDF Document</p>
            <p className='text-xs text-gray-500'>{data?.media?.name || 'Document'}</p>
          </div>
        );
      
      case 'document':
        return (
          <div className='flex h-40 w-full flex-col items-center justify-center rounded border bg-blue-50'>
            <File className='h-16 w-16 text-blue-500' />
            <p className='mt-2 text-sm font-medium text-gray-700'>Document</p>
            <p className='text-xs text-gray-500'>{data?.media?.name || 'File'}</p>
          </div>
        );
      
      case 'link':
        return (
          <div className='flex h-40 w-full flex-col items-center justify-center rounded border bg-green-50'>
            <LinkIcon className='h-16 w-16 text-green-500' />
            <p className='mt-2 text-sm font-medium text-gray-700'>External Link</p>
            <p className='text-xs text-gray-500 truncate max-w-[90%]'>{url}</p>
          </div>
        );
      
      default:
        return (
          <div className='flex h-40 w-full flex-col items-center justify-center rounded border bg-gray-50'>
            <File className='h-16 w-16 text-gray-400' />
            <p className='mt-2 text-sm font-medium text-gray-700'>File</p>
            <p className='text-xs text-gray-500'>{data?.media?.name || 'Unknown file type'}</p>
          </div>
        );
    }
  };

  return (
    <div>
      <p className='font-bold'>Media</p>

      {hasMedia ? (
        <>
          {renderMediaPreview()}
          <div className='flex w-full justify-between gap-4 bg-white p-3'>
            <div className='flex w-[85%] flex-col'>
              <p className='w-full truncate text-ellipsis'>
                {data?.media?.name ?? 'File'}
              </p>
              <p className='w-full truncate text-ellipsis text-blue-400'>
                {data?.media?.url}
              </p>
            </div>
            <div className='flex items-center justify-end gap-2 text-gray-400'>
              <Link to={data?.media?.url || '#'} target='_blank' rel='noopener noreferrer'>
                <ExternalLink size={20} className='cursor-pointer hover:text-gray-600' />
              </Link>
              <Download 
                onClick={() => handleDownload(data?.media?.url || '')} 
                size={20} 
                className='cursor-pointer hover:text-gray-600'
              />
            </div>
          </div>
        </>
      ) : (
        <div className='flex h-40 w-full flex-col items-center justify-center rounded border border-dashed border-gray-300 bg-gray-50'>
          <p className='text-sm text-gray-500'>No media available</p>
        </div>
      )}
    </div>
  );
};

export default MediaTab;
